import {
  useGetBulkDamageAreasWithHealthConditions,
  useGetBulkDamageAreasWithHealthConditionsForSite,
} from 'features/fde/assetHealthPlanning/riskCalculatorAPI'
import { NUM_YEARS_ABLE_TO_SIM_TO } from 'features/overview/utils/numYearsAbleToSimTo'
import compact from 'lodash/compact'
import uniq from 'lodash/uniq'
import {
  GeckoAssetHealthAction,
  GeckoAssetHealthPlan,
  GeckoComponent,
  GeckoUnit,
  RolodexEntryType,
} from 'types'
import { associateBy, groupBy } from 'utils/fde/maps'

import { NeededDataFetch } from './fetchRolodexData'

export function useFetchBulkHealthData(rolodexFetch: NeededDataFetch) {
  const requestPayload = useCreateDamageAreasRequestPayload(
    rolodexFetch,
    NUM_YEARS_ABLE_TO_SIM_TO
  )
  return useGetBulkDamageAreasWithHealthConditions(...requestPayload)
}

export function useFetchBulkHealthDataWithAllAssets(
  siteID: string,
  numYearsToProject: number = NUM_YEARS_ABLE_TO_SIM_TO
) {
  return useGetBulkDamageAreasWithHealthConditionsForSite(
    siteID,
    numYearsToProject,
    undefined,
    true
  )
}

const stitchComponentPreferredPlans = ({
  units,
  actionsByUnit,
  planById,
  componentsByUnit,
  numYearsToProject,
}: {
  units: readonly GeckoUnit[]
  actionsByUnit: Map<string, GeckoAssetHealthAction[]>
  planById: Map<string, GeckoAssetHealthPlan>
  componentsByUnit: Map<string, GeckoComponent[]>
  numYearsToProject: number
}): Parameters<typeof useGetBulkDamageAreasWithHealthConditions> => {
  return [
    units
      .flatMap((unit) => {
        // we need to grab the planIds this way because not all plans are tagged to units
        const actionsForUnit = actionsByUnit.get(unit.id) ?? []
        const planIdsForUnit = uniq(
          compact(
            actionsForUnit.map(
              (action) => action.tags[RolodexEntryType.COMPONENT_HEALTH_PLAN]
            )
          )
        )
        const plansForUnit = compact(
          planIdsForUnit.map((planId) => planById.get(planId))
        )
        const preferredPlan = plansForUnit.find((plan) => plan.data.preferred)

        const componentsForUnit = componentsByUnit.get(unit.id) ?? []

        return componentsForUnit.map(
          (
            component
          ): Parameters<
            typeof useGetBulkDamageAreasWithHealthConditions
          >[0] => [
            {
              componentId: component.id,
              planId: preferredPlan?.id,
            },
          ]
        )
      })
      .flat(),
    numYearsToProject,
    undefined,
    true,
  ]
}

/**
 * Creates the request payload for the `useGetBulkDamageAreasWithHealthConditions` hook.
 * @param rolodexFetch - The site view data fetch object containing all the necessary data.
 * @param numYearsToProject - the number of years to project for the damage areas [excluding current year].
 */
const useCreateDamageAreasRequestPayload = (
  rolodexFetch: NeededDataFetch,
  numYearsToProject: number
): Parameters<typeof useGetBulkDamageAreasWithHealthConditions> => {
  if (rolodexFetch.isLoading) {
    return [[]]
  }
  const actionsByUnit = groupBy({
    arr: rolodexFetch.allComponentHealthPlanActionsAtSite,
    key: `tags.${RolodexEntryType.UNIT}`,
  })

  const planById = associateBy({
    arr: rolodexFetch.allComponentHealthPlansAtSite,
    key: 'id',
  })

  const componentsByUnit = groupBy({
    arr: rolodexFetch.allComponentsAtSite,
    key: `tags.${RolodexEntryType.UNIT}`,
  })

  return stitchComponentPreferredPlans({
    units: rolodexFetch.allUnitsAtSite,
    actionsByUnit,
    planById,
    componentsByUnit,
    numYearsToProject,
  })
}
